import type { SessionKind } from "../hooks/audit/useControlSessionEnvelope";
import type { EventRun } from "../models/audit/eventRun";
import { sessionRouteBase } from "./sessionApiBase";

/**
 * The parent session an event run was dispatched against, whichever entity it
 * hangs off — read from the run's metadata so surfaces outside a session (the
 * sidebar's Background Tasks) can label and link a run without branching on
 * the metadata shape themselves.
 */
export interface EventRunParent {
  kind: SessionKind;
  /** The control session or work step session the run belongs to. */
  parentSessionId: string;
  parentSessionName: string | null;
  /** The procedure session the run generated into. */
  procedureSessionId: string;
  /** The control's or work step's name. */
  entityName: string | null;
}

/** Null for metadata that names no parent session (rows predating the keys). */
export const getEventRunParent = (
  metadata: EventRun["runMetadata"],
): EventRunParent | null => {
  if (!metadata) return null;
  if ("controlSessionId" in metadata) {
    return {
      kind: "control",
      parentSessionId: metadata.controlSessionId,
      parentSessionName: metadata.controlSessionName ?? null,
      procedureSessionId: metadata.sessionId,
      entityName: metadata.controlName ?? null,
    };
  }
  if ("workStepSessionId" in metadata) {
    return {
      kind: "work_step",
      parentSessionId: metadata.workStepSessionId,
      parentSessionName: metadata.workStepSessionName ?? null,
      procedureSessionId: metadata.sessionId,
      entityName: metadata.workStepName ?? null,
    };
  }
  return null;
};

/** The workpaper view of the run's parent session, focused on the run. */
export const eventRunWorkpaperPath = (
  parent: EventRunParent,
  eventRunId: string,
): string =>
  `${sessionRouteBase(parent.kind)}/${parent.parentSessionId}` +
  `/workpaper-view?eventRunId=${eventRunId}`;
